import React, {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {useDebouncedCallback} from 'use-debounce';

import {OnChangeInputType} from '@pluto/@types';
import useQuery from '@pluto/query-hook';
import {getOptsFromQuery} from '@pluto/select';
import {setQuery} from '@pluto/set-history-query';
import {getInitialPage, getInitialRowsPerPage, getInitialSort, Sort} from '@pluto/table';
import {UserListFilterParams} from '@pluto/users';
import {adminRoutes} from '../../App/AppRouter/routes';
import {
    handleDebounce,
    handleDebouncedCallback,
    handleMount,
    handlePaging,
    handleSort,
    handleFilters,
} from './UserListHandlers';
import UsersList from './UsersList';
import {UserContainerProps, UserListFilters, UserFiltersKeys} from './UsersListTypes';

export default function UsersListContainer(props: UserContainerProps) {
    const {setUsers, users} = props;
    const query = useQuery();
    const getFromQuery = getOptsFromQuery(query);
    const history = useHistory();
    const initialRowsPerPage = getInitialRowsPerPage(query);
    const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);
    const initialPage = getInitialPage({
        query,
        rowsPerPage,
    });
    const DEBOUNCE_TIME = 700;
    const [page, setPage] = useState<number>(initialPage);
    const initialSort = getInitialSort(query);
    const [sort, setSort] = useState<Sort>(initialSort);
    const [name, setState] = useState<string>(query.get('name') || '');
    const [filters, setFilters] = useState<UserListFilters>({
        roles: getFromQuery('roles'),
        status: getFromQuery('status'),
    });
    const params: UserListFilterParams = {
        ...filters,
        ...sort,
        name,
        offset: rowsPerPage * page,
        limit: rowsPerPage,
    };

    const filterHandlers = {
        ...params,
        history,
        route: adminRoutes.users,
        setFilters,
        setPage,
        setQuery,
        getList: setUsers,
    };

    const debouncedCallback = handleDebouncedCallback({
        ...filters,
        ...sort,
        key: 'name',
        limit: rowsPerPage,
        offset: 0,
        getList: setUsers,
        setPage,
        setQuery,
        history,
        route: adminRoutes.users,
    });

    const [debouncedFunction] = useDebouncedCallback(debouncedCallback, DEBOUNCE_TIME);

    const onDebouncedSearch: OnChangeInputType = handleDebounce({
        debouncedFunction,
        setState,
    });

    const onPaging = handlePaging(params, {
        getList: setUsers,
        setQuery,
        history,
        route: adminRoutes.users,
    });

    const onSort = handleSort({
        setSort,
        getList: setUsers,
        params,
        setQuery,
        history,
        route: adminRoutes.users,
    });

    const onRolesFilter = handleFilters({
        ...filterHandlers,
        key: UserFiltersKeys.Roles,
    });

    const onStatusFilter = handleFilters({
        ...filterHandlers,
        key: UserFiltersKeys.Status,
    });

    const onMount = handleMount({
        getList: setUsers,
        setRowsPerPage,
        setPage,
        params,
        query,
    });

    useEffect(onMount, []);

    return (
        <UsersList
            users={users}
            setSort={onSort}
            sort={sort}
            onPaging={onPaging}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            name={name}
            onSearch={onDebouncedSearch}
            onRolesFilter={onRolesFilter}
            onStatusFilter={onStatusFilter}
            filters={filters}
        />
    );
}
